import { Plan2PlateDrawer, Plan2PlateDrawerHeader } from "../Plan2PlateDrawer";
import React, { useMemo } from "react";
import { DishEditor } from "../DishEditor";
import { usePersonsAsync } from "../../queries/queries";
import { useQueryClient } from "@tanstack/react-query";
import {
  invalidateAvailabilities,
  invalidatePurchases,
  seedPurchase,
} from "../../queries/caches";
import { createByIdLookup } from "../../loaders";
import { DishModification, emptyDish } from "../../data/dishModification";
import Food from "../../data/food";
import { useToast } from "../ui/Toast";
import { showValidationMessage } from "../showValidationMessage";
import {
  createPurchaseFromDish,
  updateDefaultPurchaseFromDish,
} from "../../api";

export type DishPurchaseMode = "add" | "default";

const simpleInitialDish = (): DishModification => ({
  ...emptyDish(),
  purchaseRelevant: false,
  fromCookbook: false,
});

/**
 * Öffnet den Gericht-Editor (schlichte Variante) für ein "Aus Gericht"-
 * Lebensmittel. Im Modus "add" wird ein weiteres Kaufprodukt angelegt, im Modus
 * "default" das Standard-Kaufprodukt (und das Vorlage-Gericht) aktualisiert.
 */
export function DishPurchaseDrawer(props: {
  open: boolean;
  onClose: () => void;
  food: Food;
  initialDish?: DishModification;
  mode: DishPurchaseMode;
  onSaved?: (
    updatedFood?: Food,
    savedDish?: DishModification,
  ) => void | Promise<void>;
}) {
  return (
    <Plan2PlateDrawer
      onClose={props.onClose}
      open={props.open}
      header={
        <Plan2PlateDrawerHeader
          heading={
            props.mode === "default"
              ? "Standard-Produkt bearbeiten"
              : "Produkt aus Gericht erstellen"
          }
        />
      }
      body={<DishPurchaseDrawerBody {...props} />}
    />
  );
}

function DishPurchaseDrawerBody(props: {
  open: boolean;
  onClose: () => void;
  food: Food;
  initialDish?: DishModification;
  mode: DishPurchaseMode;
  onSaved?: (
    updatedFood?: Food,
    savedDish?: DishModification,
  ) => void | Promise<void>;
}) {
  const persons = usePersonsAsync(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const personLookup = useMemo(() => createByIdLookup(persons), [persons]);
  const loadAvailabilities = async () => {
    await invalidateAvailabilities(queryClient);
  };

  const saveOverride = async (dish: DishModification) => {
    if (!dish.description.trim()) {
      showValidationMessage(toast, "Bitte eine Bezeichnung vergeben.");
      return false;
    }
    const hasIngredient = dish.steps.some((step) => step.ingredients.length > 0);
    if (!hasIngredient) {
      showValidationMessage(toast, "Bitte mindestens eine Zutat angeben.");
      return false;
    }
    try {
      if (props.mode === "default") {
        const updatedFood = (
          await updateDefaultPurchaseFromDish({
            body: { foodId: props.food.id, dish },
          })
        ).data;
        await invalidatePurchases(queryClient);
        await props.onSaved?.(updatedFood as Food, dish);
      } else {
        const createdPurchase = await createPurchaseFromDish({
          body: { foodId: props.food.id, dish },
        });
        seedPurchase(queryClient, createdPurchase.data);
        await invalidatePurchases(queryClient);
        await props.onSaved?.();
      }
      props.onClose();
      return true;
    } catch (e) {
      showValidationMessage(toast, "Speichern fehlgeschlagen.");
      return false;
    }
  };

  return (
    <DishEditor
      isCreate={props.mode === "add"}
      variant="simple"
      initialDish={props.initialDish ?? simpleInitialDish()}
      personLookup={personLookup}
      loadAvailabilities={loadAvailabilities}
      saveOverride={saveOverride}
    />
  );
}
